"use client";

import { useMemo, useRef, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  CopyButton,
  Notice,
  Panel,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { type Columns, zipColumns } from "@/components/tools/example-columns";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  buildRhetoricalOutline,
  buildRhetoricalThesisFrames,
  MAX_PARAGRAPHS,
  MIN_PARAGRAPHS,
  RHETORICAL_STRATEGIES,
  RHETORICAL_THESIS_FRAMES,
  type RhetoricalInput,
  type RhetoricalParagraph,
  type RhetoricalStrategy,
} from "@/lib/tool-kit/lit-essays/rhetorical-outline";

/** English strings. A translated content file overrides these through `ui`. */
export const RHETORICAL_ANALYSIS_ESSAY_OUTLINE_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  situationHeading: "The passage and its rhetorical situation",
  titleLabel: "Title of the passage",
  titlePlaceholder: "e.g. the Gettysburg Address",
  authorLabel: "Author or speaker",
  authorPlaceholder: "e.g. Abraham Lincoln",
  occasionLabel: "Occasion",
  occasionPlaceholder:
    "e.g. the dedication of a Union soldiers’ cemetery at Gettysburg in November 1863",
  audienceLabel: "Audience",
  audiencePlaceholder:
    "e.g. the crowd at the ceremony and, through newspapers, the Union public",
  purposeLabel: "Purpose",
  purposePlaceholder:
    "e.g. to honor the dead and rally the Union to finish the war",
  choicesLabel: "Rhetorical choices you will analyze",
  choicesPlaceholder:
    "e.g. repetition, imagery of birth and death, and an appeal to the principle of equality",
  introHeading: "Introduction",
  contextLabel: "Opening context sentence",
  contextPlaceholder:
    "Name the author, the text, and the occasion in one sentence.",
  summaryLabel: "One-sentence summary of the passage's argument",
  summaryPlaceholder: "What does the writer say, in the order they say it?",
  thesisLabel: "Your thesis",
  thesisPlaceholder:
    "Take a position on how the writer’s choices serve their purpose.",
  framesHeading: "Thesis frames",
  framesIntro:
    "Each frame combines your situation notes — honest templates, no AI. Bracketed words mark notes you have not filled in.",
  framesNeed: "Add the author and the purpose to see thesis frames.",
  frameLabels: [
    "Choices and purpose",
    "Purpose, choices, and audience",
    "Line of reasoning — fill in the blanks",
  ],
  useAsThesis: "Use as my thesis",
  copy: "Copy",
  copied: "Copied!",
  bodyHeading: "Body paragraphs",
  paragraphLegend: "Body paragraph {n}",
  strategyLabel: "Rhetorical choice",
  claimLabel: "Claim — what the choice does",
  claimPlaceholder: "One sentence about how this choice serves the purpose.",
  evidenceLabel: "Evidence — quotations or references, one per line",
  evidencePlaceholder: "e.g. “shall not perish from the earth” (last line)",
  commentaryLabel: "Commentary — how the evidence proves the claim",
  commentaryPlaceholder:
    "Explain the effect on this audience. Do not just name the device.",
  addParagraph: "Add a body paragraph",
  removeParagraph: "Remove",
  removeParagraphLabel: "Remove body paragraph {n}",
  conclusionHeading: "Conclusion",
  significanceLabel: "Significance — the tension or the “so what”",
  significancePlaceholder:
    "What does the passage’s complexity or tension reveal about the situation?",
  significanceHint:
    "The AP Lang sophistication point rewards explaining the significance of the choices, or a purpose behind the passage’s tensions — as part of your argument.",
  strategies: {
    ethos: {
      label: "Ethos — credibility and character",
      claimPrompt:
        "Claim — say how the writer builds credibility, and why this audience needs to trust them.",
      commentaryPrompt:
        "Commentary — explain how this appeal makes the audience more willing to accept the writer's argument.",
    },
    pathos: {
      label: "Pathos — emotion",
      claimPrompt:
        "Claim — name the emotion the writer evokes and how they do it.",
      commentaryPrompt:
        "Commentary — explain how that emotion moves the audience toward the writer's purpose.",
    },
    logos: {
      label: "Logos — reasoning and evidence",
      claimPrompt:
        "Claim — say how the writer reasons: the principle, the evidence, or the steps of the argument.",
      commentaryPrompt:
        "Commentary — explain why this reasoning is persuasive for this audience.",
    },
    structure: {
      label: "Structure and organization",
      claimPrompt:
        "Claim — say how the passage is arranged and what the arrangement accomplishes.",
      commentaryPrompt:
        "Commentary — explain how the order of ideas shapes what the audience thinks or feels, and when.",
    },
    diction: {
      label: "Diction and tone",
      claimPrompt:
        "Claim — name the kind of word choice and the tone it creates.",
      commentaryPrompt:
        "Commentary — explain what the specific words suggest and what that does to the audience.",
    },
    imagery: {
      label: "Imagery and figurative language",
      claimPrompt:
        "Claim — name the image or comparison and what it makes the audience see.",
      commentaryPrompt:
        "Commentary — explain how the image supports the writer's purpose with this audience.",
    },
    syntax: {
      label: "Syntax and repetition",
      claimPrompt:
        "Claim — name the sentence pattern or repetition and its rhythm or emphasis.",
      commentaryPrompt:
        "Commentary — explain how the pattern directs attention to the writer's message.",
    },
    other: {
      label: "Another rhetorical choice",
      claimPrompt:
        "Claim — name the choice and say what it accomplishes for the writer's purpose.",
      commentaryPrompt:
        "Commentary — explain the effect on the audience, not just the technique.",
    },
  },
  fileName: "rhetorical-analysis-outline",
  document: {
    heading: "Your rhetorical analysis outline",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part of the outline has your content.",
  },
  docTitle: "Rhetorical Analysis Outline: {title}",
  docTitleBlank: "Rhetorical Analysis Outline",
  subtitle: "AP Lang-style outline",
  subtitleBy: "Passage by {author} · AP Lang-style outline",
  headings: {
    situation: "Rhetorical situation",
    introduction: "Introduction",
    body: "Body paragraph {n}: {strategy}",
    conclusion: "Conclusion",
    rubric: "Planning check against the six-point rubric",
  },
  fields: {
    author: "Author or speaker",
    occasion: "Occasion",
    audience: "Audience",
    purpose: "Purpose",
  },
  items: {
    context: "Context",
    summary: "Summary",
    thesis: "Thesis",
    claim: "Claim",
    evidence: "Evidence",
    commentary: "Commentary",
    link: "Link",
    significance: "Significance",
  },
  prompts: {
    context:
      "Context — one sentence naming the author, the text, and the occasion.",
    summary:
      "Summary — one sentence on what the writer argues, in the order they argue it.",
    thesis:
      "Thesis — take a position on how the writer's rhetorical choices serve their purpose. Do not restate the prompt.",
    evidence:
      "Evidence — quote the exact words you will analyze, with a line or paragraph reference.",
    link: "Link — tie the paragraph back to the thesis, or lead into the next choice.",
    restate:
      "Restate the thesis in fresh words — do not summarize the passage again.",
    significance:
      "Significance — explain what the writer's choices, or a tension in the passage, reveal about the rhetorical situation.",
  },
  rubricNote:
    "A planning check against the six-point AP English Language rhetorical analysis rubric, not a score. Ticks show what your outline already contains.",
  rubric: {
    thesis:
      "Thesis (1 point): takes a position on the writer's rhetorical choices",
    evidence:
      "Evidence (up to 4 points, with commentary): every body paragraph quotes specific evidence",
    commentary:
      "Commentary: every body paragraph explains how its evidence supports the claim",
    choices:
      "Commentary: at least two different rhetorical choices are analyzed",
    sophistication:
      "Sophistication (1 point): the conclusion explains significance or a tension",
  },
  missing: {
    title: "[title]",
    author: "[author]",
    audience: "[audience]",
    purpose: "[purpose]",
    choices: "[rhetorical choices]",
  },
  thesisFrames: RHETORICAL_THESIS_FRAMES,
  example: {
    title: "the Gettysburg Address",
    author: "Abraham Lincoln",
    occasion:
      "the dedication of a cemetery for Union soldiers at Gettysburg in November 1863",
    audience:
      "the crowd at the ceremony and, through newspapers, the Union public",
    purpose:
      "to honor the dead and rally the Union to finish the war for a nation dedicated to equality",
    choices:
      "repetition, imagery of birth and death, and an appeal to the principle of equality",
    context:
      "In November 1863, at the dedication of a cemetery for Union soldiers killed at Gettysburg, President Abraham Lincoln gave a speech of about two minutes after a two-hour oration by Edward Everett.",
    summary:
      "Lincoln honors the dead, says the living cannot consecrate the ground beyond what the soldiers already have, and calls on his audience to finish the unfinished work.",
    thesis:
      "In the Gettysburg Address, Abraham Lincoln uses repetition, imagery of birth and death, and an appeal to the principle of equality to honor the dead while turning his audience toward finishing the war.",
    significance:
      "A speech that says the world will “little note, nor long remember” its words became the most remembered speech of the war. The tension is deliberate: by insisting that action, not words, honors the dead, Lincoln makes his brevity itself the argument.",
  },
  exampleParagraphs: {
    claim: [
      "Lincoln's repeated “we can not” builds toward a reversal that moves the weight of the occasion from words to deeds.",
      "Images of birth and death frame the war as a test of whether the nation itself survives.",
      "Lincoln reasons from a founding principle to an obligation, so that finishing the war follows from what the nation claims to be.",
    ],
    evidence: [
      "“we can not dedicate—we can not consecrate—we can not hallow—this ground”\n“The world will little note, nor long remember what we say here, but it can never forget what they did here.”",
      "“brought forth on this continent, a new nation, conceived in Liberty”\n“shall not perish from the earth”",
      "“dedicated to the proposition that all men are created equal”\n“testing whether that nation, or any nation so conceived and so dedicated, can long endure”",
    ],
    commentary: [
      "The three verbs climb from ceremony to holiness, and the next sentence turns that humility into a claim: the soldiers have already done what the living cannot. Admitting the limits of speech builds trust and redirects the audience from admiring the speaker to continuing the soldiers' work.",
      "Opening with conception and closing with perishing makes the nation a living thing whose survival depends on the audience's resolve. That supports Lincoln's purpose: it turns a bloody war into a duty to keep something alive.",
      "Calling equality a proposition invites the audience to see the war as the test of an idea. If the nation rests on that proposition, the living must prove it true — which is the step the speech asks them to take.",
    ],
  } as Columns<"claim" | "evidence" | "commentary">,
} as const;

type Strings = typeof RHETORICAL_ANALYSIS_ESSAY_OUTLINE_STRINGS;

/**
 * The strategy each example paragraph demonstrates, by row. A token the widget reads,
 * not text a translator renders, so it stays out of the strings.
 */
const EXAMPLE_STRATEGIES: readonly RhetoricalStrategy[] = [
  "syntax",
  "imagery",
  "logos",
];

/** The example's body paragraphs, text plus strategy; English fills any short column. */
export function rhetoricalExampleParagraphs(
  strings: Strings
): Omit<RhetoricalParagraph, "id">[] {
  return zipColumns(
    RHETORICAL_ANALYSIS_ESSAY_OUTLINE_STRINGS.exampleParagraphs,
    strings.exampleParagraphs
  ).map((text, index) => ({
    strategy: EXAMPLE_STRATEGIES[index] ?? "other",
    ...text,
  }));
}

type TextKey = Exclude<keyof RhetoricalInput, "paragraphs">;

const BLANK: Record<TextKey, string> = {
  title: "",
  author: "",
  occasion: "",
  audience: "",
  purpose: "",
  choices: "",
  context: "",
  summary: "",
  thesis: "",
  significance: "",
};

const DEFAULT_STRATEGIES: readonly RhetoricalStrategy[] = [
  "pathos",
  "logos",
  "diction",
];

function ThesisFrames({
  frames,
  strings,
  onUse,
}: {
  frames: readonly string[];
  strings: Strings;
  onUse: (frame: string) => void;
}) {
  if (frames.length === 0) {
    return <Notice>{strings.framesNeed}</Notice>;
  }
  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-sm">{strings.framesIntro}</p>
      {frames.map((frame, index) => (
        <div
          className={`${RESULT_CARD} space-y-2`}
          key={strings.frameLabels[index] ?? frame}
        >
          <h3 className={WIDGET_H3}>{strings.frameLabels[index]}</h3>
          <p className="text-foreground text-sm leading-relaxed">{frame}</p>
          <div className="flex flex-wrap gap-2">
            <button
              className={BUTTON_SMALL}
              onClick={() => onUse(frame)}
              type="button"
            >
              {strings.useAsThesis}
            </button>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copy}
              small
              text={frame}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function RhetoricalAnalysisEssayOutlineWidget() {
  const strings = useToolUi(RHETORICAL_ANALYSIS_ESSAY_OUTLINE_STRINGS);
  const fmt = useToolNumber();
  const nextId = useRef(DEFAULT_STRATEGIES.length);
  const fresh = (strategy: RhetoricalStrategy): RhetoricalParagraph => {
    nextId.current += 1;
    return {
      id: nextId.current,
      strategy,
      claim: "",
      evidence: "",
      commentary: "",
    };
  };
  const [fields, setFields] = useState(BLANK);
  const [paragraphs, setParagraphs] = useState<RhetoricalParagraph[]>(() =>
    DEFAULT_STRATEGIES.map((strategy, index) => ({
      id: index + 1,
      strategy,
      claim: "",
      evidence: "",
      commentary: "",
    }))
  );
  const input: RhetoricalInput = useMemo(
    () => ({ ...fields, paragraphs }),
    [fields, paragraphs]
  );
  const doc = useMemo(
    () => buildRhetoricalOutline(input, strings),
    [input, strings]
  );
  const frames =
    fields.author.trim() !== "" && fields.purpose.trim() !== ""
      ? buildRhetoricalThesisFrames(
          input,
          strings.missing,
          strings.thesisFrames
        )
      : [];
  const update = (key: TextKey) => (value: string) =>
    setFields((previous) => ({ ...previous, [key]: value }));
  const patch = (id: number, change: Partial<RhetoricalParagraph>) =>
    setParagraphs((previous) =>
      previous.map((p) => (p.id === id ? { ...p, ...change } : p))
    );
  const area = (
    key: TextKey,
    label: string,
    placeholder: string,
    rows = 2,
    hint?: string
  ) => (
    <TextAreaField
      hint={hint}
      id={`ra-${key}`}
      label={label}
      onChange={update(key)}
      placeholder={placeholder}
      prose
      rows={rows}
      value={fields[key]}
    />
  );
  const strategyOptions = RHETORICAL_STRATEGIES.map((value) => ({
    value,
    label: strings.strategies[value].label,
  }));

  function loadExample() {
    setFields({ ...BLANK, ...strings.example });
    setParagraphs(
      rhetoricalExampleParagraphs(strings).map((example) => ({
        ...fresh("other"),
        ...example,
      }))
    );
  }

  function clear() {
    setFields(BLANK);
    setParagraphs(DEFAULT_STRATEGIES.map((strategy) => fresh(strategy)));
  }

  return (
    <div className="space-y-8">
      <div className="space-y-6 print:hidden">
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={loadExample}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button className={BUTTON_SECONDARY} onClick={clear} type="button">
            {strings.clear}
          </button>
        </div>
        <Panel title={strings.situationHeading}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="ra-title"
              label={strings.titleLabel}
              onChange={update("title")}
              placeholder={strings.titlePlaceholder}
              value={fields.title}
            />
            <TextField
              id="ra-author"
              label={strings.authorLabel}
              onChange={update("author")}
              placeholder={strings.authorPlaceholder}
              value={fields.author}
            />
          </div>
          {area("occasion", strings.occasionLabel, strings.occasionPlaceholder)}
          {area("audience", strings.audienceLabel, strings.audiencePlaceholder)}
          {area("purpose", strings.purposeLabel, strings.purposePlaceholder)}
          {area("choices", strings.choicesLabel, strings.choicesPlaceholder)}
        </Panel>
        <Panel title={strings.introHeading}>
          {area("context", strings.contextLabel, strings.contextPlaceholder)}
          {area("summary", strings.summaryLabel, strings.summaryPlaceholder)}
        </Panel>
        <section aria-live="polite" className="space-y-3">
          <h2 className={WIDGET_H2}>{strings.framesHeading}</h2>
          <ThesisFrames
            frames={frames}
            onUse={(frame) => update("thesis")(frame)}
            strings={strings}
          />
        </section>
        <Panel>
          {area("thesis", strings.thesisLabel, strings.thesisPlaceholder, 3)}
        </Panel>
        <section className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.bodyHeading}</h2>
          {paragraphs.map((paragraph, index) => (
            <fieldset className={`${CARD} space-y-4`} key={paragraph.id}>
              <legend className="px-1 font-medium text-foreground text-sm">
                {t(strings.paragraphLegend, { n: fmt(index + 1) })}
              </legend>
              <SelectField
                id={`ra-strategy-${paragraph.id}`}
                label={strings.strategyLabel}
                onChange={(strategy) => patch(paragraph.id, { strategy })}
                options={strategyOptions}
                value={paragraph.strategy}
              />
              <TextAreaField
                id={`ra-claim-${paragraph.id}`}
                label={strings.claimLabel}
                onChange={(claim) => patch(paragraph.id, { claim })}
                placeholder={strings.claimPlaceholder}
                prose
                rows={2}
                value={paragraph.claim}
              />
              <TextAreaField
                id={`ra-evidence-${paragraph.id}`}
                label={strings.evidenceLabel}
                onChange={(evidence) => patch(paragraph.id, { evidence })}
                placeholder={strings.evidencePlaceholder}
                prose
                rows={3}
                value={paragraph.evidence}
              />
              <TextAreaField
                id={`ra-commentary-${paragraph.id}`}
                label={strings.commentaryLabel}
                onChange={(commentary) => patch(paragraph.id, { commentary })}
                placeholder={strings.commentaryPlaceholder}
                prose
                rows={3}
                value={paragraph.commentary}
              />
              {paragraphs.length > MIN_PARAGRAPHS ? (
                <button
                  aria-label={t(strings.removeParagraphLabel, {
                    n: fmt(index + 1),
                  })}
                  className={BUTTON_SMALL}
                  onClick={() =>
                    setParagraphs((previous) =>
                      previous.filter((p) => p.id !== paragraph.id)
                    )
                  }
                  type="button"
                >
                  {strings.removeParagraph}
                </button>
              ) : null}
            </fieldset>
          ))}
          {paragraphs.length < MAX_PARAGRAPHS ? (
            <button
              className={BUTTON_SECONDARY}
              onClick={() =>
                setParagraphs((previous) => [...previous, fresh("ethos")])
              }
              type="button"
            >
              {strings.addParagraph}
            </button>
          ) : null}
        </section>
        <Panel title={strings.conclusionHeading}>
          {area(
            "significance",
            strings.significanceLabel,
            strings.significancePlaceholder,
            3,
            strings.significanceHint
          )}
        </Panel>
      </div>
      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="ra"
        labels={strings.document}
      />
    </div>
  );
}
